import { get } from 'utils/lodash'

// Envers revision rows from GET /api/v1/versions/work-order/{id}/exceptions — the BE
// resolves createdByUser/updatedByUser to display names, so no user lookups are needed.
export interface ExceptionAuditUser {
  username?: string | null
  firstName?: string | null
  lastName?: string | null
}

export interface ExceptionRevision {
  id?: string
  rev?: number
  revType?: 'INSERT' | 'UPDATE' | 'DELETE' | string
  createdBy?: string | null
  createdAt?: string | null
  lastModifiedBy?: string | null
  updatedAt?: string | null
  createdByUser?: ExceptionAuditUser | null
  updatedByUser?: ExceptionAuditUser | null
  confirmationStatus?: string | null
}

export interface ExceptionAuditSummary {
  createdByName: string
  createdAt: string
  updatedByName: string
  updatedAt: string
  hasUpdate: boolean
}

export const EXCEPTION_AUDIT_ACTION = {
  CREATED: 'created',
  UPDATED: 'updated',
  DELETED: 'deleted',
} as const

export type ExceptionAuditAction = typeof EXCEPTION_AUDIT_ACTION[keyof typeof EXCEPTION_AUDIT_ACTION]

export interface ExceptionAuditEntry {
  key: string
  action: ExceptionAuditAction
  userName: string
  at: string
}

/** "First Last" from a resolved audit user, falling back to the raw username/email. */
export const formatAuditUserName = (user: unknown, fallback = ''): string => {
  const fullName = [get(user, 'firstName'), get(user, 'lastName')].filter(Boolean).join(' ').trim()
  return fullName || get(user, 'username') || fallback
}

const toRevisionList = (revisions: unknown): ExceptionRevision[] => {
  if (!Array.isArray(revisions)) return []
  return [...(revisions as ExceptionRevision[])].sort((a, b) => (a.rev ?? 0) - (b.rev ?? 0))
}

const REV_TYPE_ACTION: Record<string, ExceptionAuditAction> = {
  INSERT: EXCEPTION_AUDIT_ACTION.CREATED,
  UPDATE: EXCEPTION_AUDIT_ACTION.UPDATED,
  DELETE: EXCEPTION_AUDIT_ACTION.DELETED,
}

/**
 * Created/updated summary for the card caption. Prefers the revision history; falls back to
 * audit fields on the exception payload itself so the caption still renders if the read model
 * grows createdBy/createdByUser before the versions data is available.
 */
export const getExceptionAuditSummary = (revisions: unknown, exception?: unknown): ExceptionAuditSummary => {
  const list = toRevisionList(revisions)
  const first = list[0]
  const last = list[list.length - 1]

  const createdByName = formatAuditUserName(
    get(first, 'createdByUser') ?? get(exception, 'createdByUser'),
    get(first, 'createdBy') || get(exception, 'createdBy') || ''
  )
  const createdAt = get(first, 'createdAt') || get(exception, 'createdAt') || ''

  const updatedByName = formatAuditUserName(
    get(last, 'updatedByUser') ?? get(exception, 'updatedByUser'),
    get(last, 'lastModifiedBy') || get(exception, 'lastModifiedBy') || ''
  )
  const updatedAt = get(last, 'updatedAt') || get(exception, 'updatedAt') || ''

  const hasUpdate = list.length > 1 || Boolean(createdAt && updatedAt && updatedAt !== createdAt)

  return { createdByName, createdAt, updatedByName, updatedAt, hasUpdate }
}

export interface ExceptionReviewAudit {
  status: 'confirmed' | 'rejected'
  userName: string
  at: string
}

const REVIEW_STATUS: Record<string, ExceptionReviewAudit['status']> = { CONFIRMED: 'confirmed', REJECTED: 'rejected' }

/**
 * Who moved the exception out of PENDING. Read off the revision where
 * `confirmationStatus` last changed — a later edit overwrites `lastModifiedBy`.
 */
export const getExceptionReviewAudit = (revisions: unknown): ExceptionReviewAudit | null => {
  const list = toRevisionList(revisions)
  const decided = list.reduce<ExceptionRevision | null>((found, revision, index) => {
    const status = REVIEW_STATUS[revision.confirmationStatus ?? '']
    if (!status || status === REVIEW_STATUS[list[index - 1]?.confirmationStatus ?? '']) return found
    return revision
  }, null)
  if (!decided) return null
  const rawModifier = decided.lastModifiedBy || ''
  const modifier = decided.updatedByUser ?? (rawModifier ? null : decided.createdByUser)
  return {
    status: REVIEW_STATUS[decided.confirmationStatus ?? ''],
    userName: formatAuditUserName(modifier, rawModifier || decided.createdBy || ''),
    at: decided.updatedAt || decided.createdAt || '',
  }
}

/** Full revision list for the drawer audit section, latest first. */
export const getExceptionAuditTrail = (revisions: unknown): ExceptionAuditEntry[] => {
  const list = toRevisionList(revisions)
  return list
    .map((revision, index) => {
      const action = REV_TYPE_ACTION[revision.revType ?? ''] ?? EXCEPTION_AUDIT_ACTION.UPDATED
      const isCreate = action === EXCEPTION_AUDIT_ACTION.CREATED
      const user = isCreate ? revision.createdByUser : revision.updatedByUser ?? revision.createdByUser
      const rawUser = isCreate ? revision.createdBy : revision.lastModifiedBy || revision.createdBy
      return {
        key: String(revision.rev ?? index),
        action,
        userName: formatAuditUserName(user, rawUser || ''),
        at: (isCreate ? revision.createdAt : revision.updatedAt || revision.createdAt) || '',
      }
    })
    .reverse()
}
